import { getWorkosAccessToken } from '../models/useAuth'
import { callTool } from './tools'
import {
  classifyFailure,
  classifyNameCheck,
  classifyPreview,
  parseRegisterSession,
  registerLoginUrl,
  type NameCheckResult,
  type RegisterFailure,
  type RegisterPreview,
  type RegisterSession,
} from '../utilities/registerSession'

/**
 * The client for the registration-session endpoints on the router (nobgp-router#1603).
 *
 * These are plain REST routes and not tools, so they do not go through `lib/tools.ts`: the bearer
 * is the same WorkOS access token, and that is the only part they share. The rules they feed are
 * in `utilities/registerSession.ts`, which `npm test` checks.
 *
 * ⚠⚠ NONE OF THESE ROUTES EXIST ON A RELEASED ROUTER YET. Until the router half ships, every read
 * answers a plain 404 and the page says the router cannot do this yet. That is deliberate: the
 * page must tell the reader what is wrong, and never spin.
 */
const routerApiBaseUrl =
  import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

export type RegisterResult<T> = { ok: true; value: T } | { ok: false; failure: RegisterFailure }

const sessionUrl = (sessionId: string) =>
  `${routerApiBaseUrl}/v1/register/session/${encodeURIComponent(sessionId)}`

/** Where an unsigned reader goes. See `registerLoginUrl` for why this is not the app's `/login`. */
export const loginUrlFor = (sessionId: string, appOrigin: string) =>
  registerLoginUrl(routerApiBaseUrl, appOrigin, sessionId)

/**
 * The answer, both ways: the parsed JSON when it is JSON, and the raw text either way.
 *
 * ⚠⚠ THE TEXT IS LOAD-BEARING. The router refuses with `http.Error`, so an expired record, an
 * unknown network and a router that has no such route ALL answer `text/plain` — "it did not parse
 * as JSON" cannot tell them apart. `classifyFailure` reads the sentence. An empty body (the
 * approval's `204`) parses as neither, and that is a success rather than a refusal.
 */
const readBody = async (response: Response): Promise<{ json: unknown; text: string }> => {
  const text = await response.text().catch(() => '')
  try {
    return { json: text ? JSON.parse(text) : null, text }
  } catch {
    return { json: null, text }
  }
}

const bearer = (): string | null => getWorkosAccessToken()

/** Reads the record. The page calls this on a timer until the machine finishes. */
export const fetchRegisterSession = async (sessionId: string): Promise<RegisterResult<RegisterSession>> => {
  const token = bearer()
  if (!token) return { ok: false, failure: classifyFailure(401) }

  let response: Response
  try {
    response = await fetch(sessionUrl(sessionId), {
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch (error) {
    console.warn('register: failed to read the session', error)
    return { ok: false, failure: classifyFailure(0) }
  }

  const body = await readBody(response)
  if (!response.ok) return { ok: false, failure: classifyFailure(response.status, body.json, body.text) }
  return { ok: true, value: parseRegisterSession(body.json) }
}

/**
 * Sends the reader's approval. One shot: the router refuses a second one.
 *
 * The body is built by `approvalBody`, which decides between `network_id` and a name and leaves
 * an empty field OUT rather than sending it empty. An absent `node_name` keeps the name the router
 * proposed, which is the machine's hostname.
 *
 * The value is the record when the router sent one and null when it did not — see below, because
 * today it does not.
 */
export const submitRegisterApproval = async (
  sessionId: string,
  approval: Record<string, string | boolean>
): Promise<RegisterResult<RegisterSession | null>> => {
  const token = bearer()
  if (!token) return { ok: false, failure: classifyFailure(401) }

  let response: Response
  try {
    response = await fetch(sessionUrl(sessionId), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(approval),
    })
  } catch (error) {
    console.warn('register: failed to send the approval', error)
    return { ok: false, failure: classifyFailure(0) }
  }

  const body = await readBody(response)
  if (!response.ok) return { ok: false, failure: classifyFailure(response.status, body.json, body.text) }
  // ⚠⚠ THE ROUTER ANSWERS `204` WITH NO BODY (router#1604 `chooseRegisterSession`), so there is
  // usually nothing to parse. Parsing it anyway hands the page an EMPTY record — no networks, no
  // approval, none of the facts about the machine — which renders as "you have no network for this
  // machine yet" one moment after a successful approval. Null means "accepted, ask again".
  return { ok: true, value: body.json ? parseRegisterSession(body.json) : null }
}

/**
 * The reader's Deny: `POST …/deny` (nobgp-router#1896). The router records who denied it, and the
 * terminal's poll then gets 410 with "registration denied in the browser by <email>". A
 * registration that already produced its node answers 409.
 */
export const denyRegisterSession = async (sessionId: string): Promise<'denied' | 'registered' | 'failed'> => {
  const token = bearer()
  if (!token) return 'failed'
  try {
    const response = await fetch(`${sessionUrl(sessionId)}/deny`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (response.ok || response.status === 410) return 'denied'
    if (response.status === 409) return 'registered'
  } catch (error) {
    console.warn('register: failed to deny', error)
  }
  return 'failed'
}

/**
 * What the machine is, BEFORE sign-in: `GET …/preview`, with no bearer.
 *
 * ⚠ The preview binds nothing. Only the authenticated GET binds the session to a user, so this is
 * safe to read with no session. Never throws: the preview is a courtesy, and a failure falls back
 * to the generic sentence rather than stopping the sign-in.
 */
export const fetchRegisterPreview = async (sessionId: string): Promise<RegisterPreview> => {
  try {
    const response = await fetch(`${sessionUrl(sessionId)}/preview`)
    const body = await readBody(response)
    return classifyPreview(response.status, body.json, body.text)
  } catch (error) {
    console.warn('register: failed to read the preview', error)
    return { kind: 'generic' }
  }
}

/**
 * Makes a network for the machine, with the same tool the rest of the app uses
 * (`useNetwork.add`). Not through the store: this page is outside the app shell, and the org is
 * the one the reader picked HERE, not the app's active org. An empty `orgId` (an older router,
 * which sends no orgs) lets the router use the caller's personal org. Throws a `ToolCallError`,
 * which the caller turns into a `CreateRefusal`.
 */
export const createNetwork = async (name: string, orgId: string): Promise<string | null> => {
  const res = await callTool<{ network_id?: string }>('network_create', { name, org_id: orgId || undefined })
  return res?.network_id ?? null
}

/**
 * Asks whether a name is free in a network: `GET …/name?network_id=&name=`, with the bearer (the
 * router answers only the session's owner).
 *
 * Never throws, and every failure is `none` — see `classifyNameCheck`. The check is advice; the
 * approval checks the name again and is the one that decides.
 */
export const fetchNameCheck = async (
  sessionId: string,
  networkId: string,
  name: string,
  signal?: AbortSignal
): Promise<NameCheckResult> => {
  const token = bearer()
  if (!token) return { kind: 'none' }
  const query = new URLSearchParams({ network_id: networkId, name })
  try {
    const response = await fetch(`${sessionUrl(sessionId)}/name?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    })
    const body = await readBody(response)
    return classifyNameCheck(response.status, body.json, { name, networkId })
  } catch (error) {
    if ((error as { name?: string } | null)?.name !== 'AbortError') console.warn('register: failed to check the name', error)
    return { kind: 'none' }
  }
}
